import React from "react";
import * as Icons from "lucide-react";
import { FEATURES, SHOWCASE_IMAGES } from "../mock";

const tintMap = {
  emerald: "from-emerald-100 to-emerald-50 text-emerald-800",
  amber: "from-amber-100 to-amber-50 text-amber-800",
  sky: "from-sky-100 to-sky-50 text-sky-800",
  rose: "from-rose-100 to-rose-50 text-rose-800",
  violet: "from-violet-100 to-violet-50 text-violet-800",
  orange: "from-orange-100 to-orange-50 text-orange-800",
};

const Features = () => {
  return (
    <section id="features" className="relative py-24 md:py-32 bg-[#f6f1e7]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-900/70 font-semibold mb-4">Why Splitara</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-emerald-950 leading-[1.05]">
            Designed for the part of travel apps <span className="italic text-emerald-700">people actually fight about.</span>
          </h2>
          <p className="mt-5 text-emerald-950/70 text-lg max-w-2xl">
            Most planners stop at places and dates. Splitara connects planning with money, membership, messaging, confirmations, and memories — so your group has one operational source of truth.
          </p>
        </div>

        {/* Duo showcase cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          <div className="group relative rounded-3xl overflow-hidden aspect-[16/10] shadow-sm hover:shadow-xl transition">
            <img src={SHOWCASE_IMAGES.planTogether} alt="Plan together" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/85 via-emerald-950/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-7 md:p-8 text-white">
              <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">Plan together</div>
              <div className="font-display text-2xl md:text-3xl font-semibold">Shared itineraries and member coordination</div>
            </div>
          </div>
          <div className="group relative rounded-3xl overflow-hidden aspect-[16/10] shadow-sm hover:shadow-xl transition">
            <img src={SHOWCASE_IMAGES.splitClearly} alt="Split clearly" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-orange-950/85 via-orange-950/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-7 md:p-8 text-white">
              <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">Split clearly</div>
              <div className="font-display text-2xl md:text-3xl font-semibold">Balances, settlements, exports</div>
            </div>
          </div>
        </div>

        {/* App suite */}
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-900/70 font-semibold mb-3">App suite</div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold text-emerald-950 max-w-xl leading-tight">
              A modern travel workspace for groups — not just a pretty checklist.
            </h3>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.Star;
            return (
              <div
                key={i}
                className="group rounded-2xl bg-white border border-black/5 p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tintMap[f.color]} grid place-items-center mb-5 group-hover:scale-110 transition`}>
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <h4 className="font-display text-xl font-semibold text-emerald-950 mb-2">{f.title}</h4>
                <p className="text-emerald-950/70 leading-relaxed text-[15px]">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
